import type { FavoriteView } from '@/api/favorites'
import { useAuthContext } from '@offer/auth-lib'
import { VpButton } from '@vtmn-play/react'
import { VpLoader } from '@vtmn-play/react/headless'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { CHECK_EMOJI_UNIFIED } from '@/components/FavoriteViews/FavoriteViewEmoji'
import { ALL_SAVED_VIEWS_ID } from '@/components/FavoriteViews/FavoriteViewFolderCard'
import { FavoriteViewFolderDrawer } from '@/components/FavoriteViews/FavoriteViewFolderDrawer'
import { CountriesProvider } from '@/contexts/countries/CountriesProvider'
import { useCountriesContext } from '@/contexts/countries/useCountriesContext'
import { buildFavoriteViewUrl } from '@/contexts/favorites/buildFavoriteViewUrl'
import { useToasterContext } from '@/contexts/toaster/useToasterContext'
import {
  useFavoriteViewFolders,
  useFavoriteViews,
} from '@/hooks/favorites/useFavoriteViews'
import { useParameters } from '@/hooks/useParameters'
import { FavoriteViewsFolderDetail } from './FavoriteViewsFolderDetail'
import { FavoriteViewsFolderOverview } from './FavoriteViewsFolderOverview'
import { FavoriteViewsManagementModal } from './FavoriteViewsManagementModal'
import { FavoriteViewsPageActionsProvider } from './FavoriteViewsPageActionsProvider'
import { useFavoriteViewsPageActions } from './useFavoriteViewsPageActions'
import './favorite-views-page.scss'

function FavoriteViewsPageContent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { folderRoute } = useParams()
  const { totalCountries, getCountryByCode, normalizeCountryCodes } = useCountriesContext()
  const { showToast } = useToasterContext()
  const routeFolderId = folderRoute?.startsWith('folder=')
    ? folderRoute.slice('folder='.length)
    : undefined
  const folderId = routeFolderId && routeFolderId !== 'all' ? routeFolderId : undefined
  const isFolderOverview = !routeFolderId
  const viewsQuery = useFavoriteViews(true, folderId)
  const foldersQuery = useFavoriteViewFolders()

  const folders = foldersQuery.data?.items ?? []
  const views = viewsQuery.data?.items ?? []
  const allViewCount = viewsQuery.data?.total ?? 0
  const selectedFolder = routeFolderId === 'all'
    ? { id: ALL_SAVED_VIEWS_ID, name: t('favorite-views.panel.all-saved-views'), emoji: CHECK_EMOJI_UNIFIED, viewCount: allViewCount }
    : folders.find(folder => folder.id === folderId)
  const hasError = viewsQuery.isError || foldersQuery.isError

  const showError = () => showToast({ variant: 'error', message: t('favorite-views.errors.manage') })

  const {
    activeModal,
    closeFolderDrawer,
    closeModal,
    editingFolder,
    handleModalClosed,
    isDeleteFolderPending,
    isDeleteViewPending,
    isFolderDrawerOpen,
    isModalOpen,
    openCreateFolder,
    openDeleteFolder,
    openDeleteView,
    openFolderEditor,
    renameView,
    requestShareLink,
    saveFolder,
    validateActiveModal,
  } = useFavoriteViewsPageActions({
    onError: showError,
    onFolderDeleted: () => {
      if (routeFolderId && routeFolderId !== 'all')
        void navigate('/favorites')
    },
  })

  const openFolder = (nextFolderId: string) => navigate(`/favorites/folder=${encodeURIComponent(nextFolderId)}`)

  const onBack = () => navigate('/favorites')

  const getCountryLabel = (view: FavoriteView) => {
    const countryCodes = normalizeCountryCodes(view.search.filters.homologation_countries ?? [])

    if (countryCodes.length === 0 || (totalCountries > 0 && countryCodes.length >= totalCountries))
      return t('products-page.all-countries')
    if (countryCodes.length === 1)
      return getCountryByCode(countryCodes[0])?.name ?? countryCodes[0]
    return t('products-page.n-countries-selected', { count: countryCodes.length })
  }

  const openView = (view: FavoriteView) => {
    if (view.hierarchyIssue) {
      showToast({ variant: 'error', message: t('favorite-views.errors.hierarchy') })
      return
    }

    const url = buildFavoriteViewUrl(view.search)
    if (url)
      void navigate(url)
    else
      showError()
  }

  const pageActions = {
    onOpenFolder: openFolder,
    onBack,
    onOpenView: openView,
    onRenameView: renameView,
    onDeleteView: openDeleteView,
    onShareView: requestShareLink,
    onRenameFolder: openFolderEditor,
    onDeleteFolder: openDeleteFolder,
  }

  return (
    <section className="favorite-views-page" data-testid="favorite-views-page">
      {isFolderOverview && (
        <header className="favorite-views-page__heading">
          <h1>{t('favorite-views.page.title')}</h1>
          <VpButton variant="primary" onClick={openCreateFolder}>
            <span aria-hidden="true">+</span>
            {t('favorite-views.panel.add-folder')}
          </VpButton>
        </header>
      )}
      {hasError && <p className="favorite-views-page__error" role="alert">{t('favorite-views.errors.load-page')}</p>}
      <FavoriteViewsPageActionsProvider value={pageActions}>
        {isFolderOverview
          ? (
              <FavoriteViewsFolderOverview
                isLoading={viewsQuery.isLoading || foldersQuery.isLoading}
                folders={folders}
                allViewCount={allViewCount}
              />
            )
          : (
              <FavoriteViewsFolderDetail
                isLoading={viewsQuery.isLoading || (routeFolderId !== 'all' && foldersQuery.isLoading)}
                selectedFolder={selectedFolder}
                views={views}
                getCountryLabel={getCountryLabel}
              />
            )}
      </FavoriteViewsPageActionsProvider>
      {isFolderDrawerOpen && (
        <FavoriteViewFolderDrawer
          onClose={closeFolderDrawer}
          onSave={saveFolder}
          isEditing={Boolean(editingFolder)}
          initialName={editingFolder?.name}
          initialEmoji={editingFolder?.emoji}
        />
      )}
      <FavoriteViewsManagementModal
        activeModal={activeModal}
        isOpen={isModalOpen}
        isDeleteFolderPending={isDeleteFolderPending}
        isDeleteViewPending={isDeleteViewPending}
        onClose={handleModalClosed}
        onCancel={closeModal}
        onValidate={validateActiveModal}
      />
    </section>
  )
}

export function FavoriteViewsPage() {
  const { user } = useAuthContext()
  const parametersQuery = useParameters(user?.id)
  const hasFavoriteAccess = parametersQuery.data?.some(parameter =>
    parameter.parameter_name === 'ACCESS_FAVORITES' && parameter.parameter_value === 'true',
  ) ?? false

  if (parametersQuery.isLoading)
    return <div className="favorite-views-page__loading"><VpLoader size="large" /></div>

  if (!hasFavoriteAccess)
    return null

  return (
    <CountriesProvider>
      <FavoriteViewsPageContent />
    </CountriesProvider>
  )
}
